import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, InputAdornment,
  Chip, Avatar, Alert, CircularProgress, IconButton, Tooltip, TablePagination,
  MenuItem,
} from '@mui/material';
import { Add, Search, PersonOff, Edit, PersonAdd } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { staffService } from '@smartgrow/shared';
import type { Staff } from '@smartgrow/shared/types';

const roleColor = (role: string) => role === 'ADMIN' ? 'primary' : 'default';

export const StaffPage = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('ALL');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [page, setPage] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(searchQuery); setPage(0); }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['staff', page, roleFilter, statusFilter, debouncedSearch],
    queryFn: () => staffService.search({
      page, size: 10,
      search: debouncedSearch || undefined,
      role: roleFilter === 'ALL' ? undefined : roleFilter,
      active: statusFilter === 'ALL' ? undefined : statusFilter === 'ACTIVE',
    }),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => staffService.deleteStaff(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['staff'] }),
  });

  const activateMutation = useMutation({
    mutationFn: (id: string) => staffService.activateStaff(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['staff'] }),
  });

  const filtered = data?.content ?? [];

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" fontWeight="bold">Staff Members</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/dashboard/staff/new')}>
          Add Staff
        </Button>
      </Box>

      <Paper sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search staff by name, username or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            sx={{ flex: 1, minWidth: 220 }}
          />
          <TextField select size="small" label="Role" value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)} sx={{ minWidth: 140 }}>
            <MenuItem value="ALL">All</MenuItem>
            <MenuItem value="OWNER">Owner</MenuItem>
            <MenuItem value="ADMIN">Admin</MenuItem>
            <MenuItem value="SUPERVISOR">Supervisor</MenuItem>
            <MenuItem value="WORKER">Worker</MenuItem>
          </TextField>
          <TextField select size="small" label="Status" value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 130 }}>
            <MenuItem value="ALL">All</MenuItem>
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="INACTIVE">Inactive</MenuItem>
          </TextField>
        </Box>
      </Paper>

      {isError && <Alert severity="error" sx={{ mb: 2 }}>Failed to load staff members.</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Staff Member</TableCell>
              <TableCell>Username</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell>
              </TableRow>
            ) : filtered?.length ? (
              filtered.map((member, i) => (
                <TableRow key={member.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/dashboard/staff/${member.id}`)}>
                  <TableCell>{page * 10 + i + 1}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14 }}>
                        {member.fullName.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                      </Avatar>
                      <Typography variant="body2" fontWeight="medium">{member.fullName}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{member.username}</TableCell>
                  <TableCell>{member.email}</TableCell>
                  <TableCell>{(member as Staff).phone ?? '-'}</TableCell>
                  <TableCell>
                    <Chip label={member.role} color={roleColor(member.role) as any} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={member.active ? 'Active' : 'Inactive'}
                      color={member.active ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/staff/${member.id}/edit`); }}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {member.active ? (
                        <Tooltip title="Deactivate">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (globalThis.confirm(`Deactivate ${member.fullName}?`)) {
                                deactivateMutation.mutate(member.id);
                              }
                            }}
                          >
                            <PersonOff fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Activate">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={(e) => {
                              e.stopPropagation();
                              activateMutation.mutate(member.id);
                            }}
                          >
                            <PersonAdd fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>No staff members found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={data?.totalElements ?? 0}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={10}
          rowsPerPageOptions={[10]}
        />
      </TableContainer>
    </Box>
  );
};
